import { Link } from "react-router-dom";
import { Check, ArrowRight, MapPin, FileText } from "lucide-react";
import POLIGONI from "@/data/poligoni.json";
import { IMAGES } from "@/data/site";
import { PRODUCTS } from "@/data/products";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import CTASection from "@/components/CTASection";
import { Eyebrow, usePageTitle } from "@/components/PageHeader";

const Header = () => (
  <section data-testid="poligoni-header" className="relative overflow-hidden border-b border-white/10 pt-[116px] md:pt-[72px]">
    <img src={IMAGES.poligono} alt="Linee di tiro di un poligono indoor" className="absolute inset-0 h-full w-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
    <div className="relative mx-auto max-w-[1600px] px-6 pb-20 pt-24 md:px-12 md:pb-28 md:pt-32">
      <Reveal>
        <Eyebrow>{POLIGONI.eyebrow}</Eyebrow>
        <h1 className="max-w-5xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-7xl">
          {POLIGONI.h1}
          <span className="text-brand">.</span>
        </h1>
        <div className="mt-8 max-w-3xl space-y-4 text-base leading-relaxed text-white/65 md:text-lg">
          {POLIGONI.intro.map((t) => <p key={t.slice(0, 20)}>{t}</p>)}
        </div>
        <Link to="/contatti" data-testid="poligoni-hero-quote" className="mt-10 inline-flex items-center gap-3 rounded-md bg-brand px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-hover">
          <FileText className="h-4 w-4" /> Richiedi un sopralluogo
        </Link>
      </Reveal>
    </div>
  </section>
);

const Services = () => (
  <section data-testid="poligoni-services" className="bg-ink">
    <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
      <Reveal>
        <Eyebrow>Cosa facciamo</Eyebrow>
        <h2 className="max-w-4xl font-display text-3xl font-extrabold uppercase leading-[0.95] text-white md:text-6xl">
          Interventi sui poligoni di tiro
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {POLIGONI.services.map((s, i) => (
          <Reveal key={s.id} delay={(i % 4) * 100} className="bg-ink p-8" data-testid={`poligoni-service-${s.id}`}>
            <span className="font-display text-sm font-bold text-brand">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-5 font-display text-xl font-bold uppercase text-white">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/55">{s.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const List = ({ title, items, testId }) => (
  <Reveal data-testid={testId} className="rounded-xl border border-white/10 bg-ink p-8 md:p-10">
    <h2 className="font-display text-2xl font-bold uppercase text-white md:text-3xl">{title}</h2>
    <ul className="mt-6 space-y-3">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3 text-sm text-white/75">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {t}
        </li>
      ))}
    </ul>
  </Reveal>
);

const Maintenance = () => (
  <section className="border-t border-white/10 bg-surface">
    <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-6 px-6 py-24 md:px-12 lg:grid-cols-3">
      <List title="Manutenzione ordinaria" items={POLIGONI.ordinaria} testId="poligoni-ordinaria" />
      <List title="Manutenzione straordinaria" items={POLIGONI.straordinaria} testId="poligoni-straordinaria" />
      <Reveal data-testid="poligoni-area" className="rounded-xl border border-brand/30 bg-brand/10 p-8 md:p-10">
        <MapPin className="h-6 w-6 text-brand" />
        <h2 className="mt-5 font-display text-2xl font-bold uppercase text-white md:text-3xl">{POLIGONI.area.title}</h2>
        <p className="mt-4 text-sm leading-relaxed text-white/70">{POLIGONI.area.text}</p>
        <Link to="/contatti" data-testid="poligoni-area-contact" className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand hover:text-brand-hover">
          Contattaci <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Products = () => (
  <section data-testid="poligoni-products" className="border-t border-white/10 bg-ink">
    <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12">
      <Eyebrow>Materiali e ricambi</Eyebrow>
      <h2 className="font-display text-3xl font-extrabold uppercase leading-[0.95] text-white md:text-5xl">
        Prodotti per poligoni di tiro
      </h2>
      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PRODUCTS.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
      <Link to="/shop" data-testid="poligoni-shop-link" className="mt-10 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand hover:text-brand-hover">
        Vai al catalogo completo <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </section>
);

export default function ManutenzionePoligoni() {
  usePageTitle(POLIGONI.path);
  return (
    <main data-testid="poligoni-page">
      <Header />
      <Services />
      <Maintenance />
      <Products />
      <CTASection />
    </main>
  );
}
